import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subject } from "rxjs";
import { SilentSwUpdateService } from "../../../global-service/silent-sw-update.service";
import { LoggerService } from "../../../../ancestors/logger.service";

/**
 * Componente invisibile per la gestione automatica degli aggiornamenti del Service Worker
 * Funziona silenziosamente in background, controllando e applicando aggiornamenti automaticamente
 * Logga tutte le operazioni sulla console senza mostrare UI all'utente
 */
@Component({
  selector: "app-service-worker-update",
  standalone: true,
  imports: [CommonModule],
  template: "",
  styles: []
})
export class ServiceWorkerUpdateComponent implements OnInit, OnDestroy {
  private silentSwUpdateService = inject(SilentSwUpdateService);
  private logger = inject(LoggerService);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initializeSilentUpdates().catch(error => {
      this.logger.error(`[ServiceWorkerUpdateComponent] Error initializing silent updates: ${error instanceof Error ? error.message : String(error)}`);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Pulisci il servizio
    this.silentSwUpdateService.destroy();
  }

  /**
   * Inizializza il sistema di aggiornamento silenzioso
   */
  private async initializeSilentUpdates(): Promise<void> {
    try {
      
      // Inizializza il servizio silenzioso
      await this.silentSwUpdateService.initialize();
      
      // Ottieni e logga lo status iniziale
      const status = await this.silentSwUpdateService.getCurrentStatus();
      this.logger.debug(`[ServiceWorkerUpdateComponent] Initial SW status: ${JSON.stringify(status)}`);
      
      // Configura intervallo di controllo (ogni 5 minuti)
      this.silentSwUpdateService.setUpdateCheckInterval(5);
      
    } catch (error) {
      this.logger.error(`[ServiceWorkerUpdateComponent] Errore durante l'inizializzazione del sistema silenzioso: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Metodo pubblico per forzare un controllo aggiornamenti (per debug)
   * Può essere chiamato dalla console del browser per test manuali
   */
  public async forceUpdateCheck(): Promise<void> {
    await this.silentSwUpdateService.forceUpdateCheck();
  }

  /**
   * Metodo pubblico per ottenere lo status corrente (per debug)
   * Può essere chiamato dalla console del browser per verifiche
   */
  public async getCurrentStatus(): Promise<{ connected: boolean; version: string | null }> {
    return this.silentSwUpdateService.getCurrentStatus();
  }
}
